import React, { useState, useEffect, useMemo, useCallback } from "react";
import FormDialog from "./FormDialog";
import ContactForm from "./ContactForm";
import { EmailBody } from "./Inbox";

const ORG_TYPES = ["Operator", "NOC", "Government", "Regulator", "Principal", "Partner", "DFI", "Investor", "Contractor", "Other"];
import type { Partner, Contact, Org, Deal, Email, Minutes, Todo } from "../types";
import { STAGES } from "../types";
import { useData } from "../hooks/useData";
import { api } from "../api/client";
import { I } from "./Icons";
import {
  Avatar,
  AvatarStack,
  clsx,
  fmtDate,
  dueText,
  dueOverdue,
  HeatMeter,
  OWNER_LABEL,
} from "./Primitives";

/* ---- Helpers ---- */

function initials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

const SIDE_TYPE: Record<string, string> = {
  operator: "Operator",
  noc: "NOC",
  government: "Government",
  principal: "Principal",
  regulator: "Regulator",
};

const ACC_TYPE_C: Record<string, string> = {
  Operator: "#5b6b7d",
  NOC: "#2D7D9A",
  Government: "#3B5B92",
  Regulator: "#9A3F5B",
  Principal: "#0E7C66",
  External: "#8a93a0",
  Account: "#5b6b7d",
};

/* ---- Account model built client-side from orgs + contacts + deals ---- */

interface Account {
  id: string;
  name: string;
  type: string;
  note: string;
  markets: string[];
  contactIds: string[];
  dealIds: string[];
  owners: string[];          // relationship owners on the account itself
}

/** Marker for "add a new contact" in the edit-contact state. */
const NEW_CONTACT = { id: "__new__" } as Contact;

function accIdOfContact(c: Contact): string {
  return c.org_id || "x:" + (c.org_name || "External");
}

function buildAccounts(
  orgs: Org[],
  contacts: Contact[],
  deals: Deal[]
): Account[] {
  const map: Record<string, Account> = {};

  const ensure = (id: string, base: Partial<Account>): Account => {
    if (!map[id])
      map[id] = {
        id,
        name: base.name || id,
        type: base.type || "Account",
        note: base.note || "",
        owners: base.owners || [],
        markets: [],
        contactIds: [],
        dealIds: [],
      };
    return map[id];
  };

  // Seed from known orgs
  orgs.forEach((o) => ensure(o.id, { name: o.name, type: o.type, note: o.note, owners: o.relationship_owners || [] }));

  // From contacts (newsletter leads without a company live on the Leads screen)
  contacts.forEach((c) => {
    if (c.side === "lead" && !c.org_id && !c.org_name) return;
    const id = accIdOfContact(c);
    const orgName = c.org_name || "External";
    const a = ensure(id, {
      name: orgName,
      type: SIDE_TYPE[c.side] || "External",
      note: "",
    });
    if (a.contactIds.indexOf(c.id) < 0) a.contactIds.push(c.id);
    if (c.market && a.markets.indexOf(c.market) < 0) a.markets.push(c.market);
  });

  // From deals (operator orgs)
  deals.forEach((d) => {
    if (!d.operator_id) return;
    const org = orgs.find((o) => o.id === d.operator_id);
    const a = ensure(d.operator_id, {
      name: org?.name || d.operator_name || d.operator_id,
      type: org?.type || "Account",
      note: org?.note || "",
    });
    if (a.dealIds.indexOf(d.id) < 0) a.dealIds.push(d.id);
    if (d.market && a.markets.indexOf(d.market) < 0) a.markets.push(d.market);
  });

  // Real organisations always show; loose "org name on a contact" accounts only while they have contacts.
  return Object.values(map).filter(
    (a) => !a.id.startsWith("x:") || a.contactIds.length > 0
  );
}

/* ---- Communications model ---- */

interface CommItem {
  kind: "email" | "minutes" | "note";
  date: string;
  dir?: string;
  subject?: string;
  text?: string;
  who?: string;
  id?: string;
}

function buildAccountComms(
  account: Account,
  contacts: Contact[],
  emails: Email[],
  minutes: Minutes[],
  contactFilter: string | null
): CommItem[] {
  const cids = account.contactIds;
  const contactNames = contacts
    .filter((c) => cids.includes(c.id))
    .map((c) => c.name);
  const out: CommItem[] = [];

  // Emails linked to this account's contacts or deals
  emails.forEach((e) => {
    const matchesDeal = e.deal_id && account.dealIds.includes(e.deal_id);
    const matchesContact =
      contactNames.includes(e.from_name) ||
      (e.deal_id && account.dealIds.includes(e.deal_id));
    if (!matchesDeal && !matchesContact) return;

    const cName = e.from_name;
    if (contactFilter && cName !== contactFilter) return;

    out.push({
      kind: "email",
      date: e.date,
      dir: e.status === "private" ? "out" : (e.from_email?.includes("saga") ? "out" : "in"),
      subject: e.subject,
      text: e.preview || e.body?.slice(0, 140),
      who: cName,
      id: e.id,
    });
  });

  // Minutes linked to this account's deals or contacts
  minutes.forEach((m) => {
    const matchesDeal = m.deal_id && account.dealIds.includes(m.deal_id);
    const matchesContact = m.contact_id && cids.includes(m.contact_id);
    const matchesOrg = (m.org_ids || []).includes(account.id) || m.org_id === account.id;
    if (!matchesDeal && !matchesContact && !matchesOrg) return;

    const contact = m.contact_id
      ? contacts.find((c) => c.id === m.contact_id)
      : null;
    const cName = contact?.name;
    if (contactFilter && cName !== contactFilter) return;

    out.push({
      kind: "minutes",
      date: m.date,
      subject: m.title,
      text: m.summary,
      who: cName,
      id: m.id,
    });
  });

  return out.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
}

/* ---- Sub-components ---- */

function CommsTimeline({
  comms,
  onNew,
  go,
}: {
  comms: CommItem[];
  onNew: () => void;
  go: (screen: string, id?: string) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [full, setFull] = useState<Email | null>(null);
  const openItem = async (c: CommItem) => {
    if (c.kind === "minutes" && c.id) { window.open(`/api/minutes/${c.id}/doc`, "_blank"); return; }
    if (c.kind !== "email" || !c.id) return;
    if (openId === c.id) { setOpenId(null); return; }
    setOpenId(c.id); setFull(null);
    try { setFull(await api.get<Email>(`/api/emails/${c.id}`)); } catch { /* shown as loading failure below */ }
  };
  if (!comms.length)
    return (
      <div className="cd-empty">
        <p>Radio silence — no comms logged yet. Time to pick up the phone?</p>
        <button className="btn primary sm" onClick={onNew}>
          {I.note({ size: 14, w: 1.8 })}
          Add minutes from a call
        </button>
      </div>
    );

  const META: Record<string, { ic: (p?: any) => React.ReactElement; label: string }> = {
    email: { ic: I.mail, label: "Email" },
    minutes: { ic: I.note, label: "Minutes" },
    note: { ic: I.note, label: "Note" },
  };

  return (
    <div className="comms-timeline">
      {comms.map((c, i) => {
        const m = META[c.kind] || META.note;
        const dir =
          c.kind === "email"
            ? c.dir === "out"
              ? "Sent"
              : "Received"
            : m.label;
        return (
          <div key={i} className={clsx("comm-row", (c.kind === "email" || c.kind === "minutes") && c.id && "clickable", openId === c.id && "open")}
            role={c.id ? "button" : undefined} tabIndex={c.id ? 0 : undefined}
            onClick={() => openItem(c)} onKeyDown={e => e.key === "Enter" && openItem(c)}>
            <div
              className={clsx(
                "comm-ic",
                "comm-" + c.kind,
                c.dir === "out" && "out"
              )}
            >
              {m.ic({ size: 14, w: 1.8 })}
            </div>
            <div className="comm-body">
              <div className="comm-top">
                <span className={clsx("comm-kind", "ck-" + c.kind)}>{dir}</span>
                {c.who && <span className="comm-who">{c.who}</span>}
                <span className="comm-date mono">
                  {fmtDate(c.date, { day: "numeric", month: "short" })}
                </span>
              </div>
              {c.subject && <div className="comm-subj">{c.subject}</div>}
              {openId === c.id && c.kind === "email" ? (
                <div className="comm-full" onClick={e => e.stopPropagation()}>
                  {full?.id === c.id ? <EmailBody email={full} /> : <div className="comm-text">Opening…</div>}
                  <div className="comm-full-foot">
                    <button className="btn ghost sm" onClick={() => go("inbox", c.id)}>{I.mail({ size: 13 })}Open in Inbox (reply, route)</button>
                    <button className="btn ghost sm" onClick={() => setOpenId(null)}>Close</button>
                  </div>
                </div>
              ) : c.text && <div className="comm-text">{c.text}</div>}
              {c.kind === "minutes" && c.id && <div className="comm-hint">Click to open the minutes</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ContactsList({
  contacts,
  partners,
  onEmail,
  onComms,
  onEdit,
}: {
  contacts: Contact[];
  partners: Record<string, Partner>;
  onEmail: (c: Contact) => void;
  onComms: (c: Contact) => void;
  onEdit: (c: Contact) => void;
}) {
  if (!contacts.length)
    return (
      <div className="cd-empty">
        <p>No contacts yet — every relationship starts with a name.</p>
      </div>
    );
  return (
    <div className="contacts-grid">
      {contacts.map((c) => (
        <div
          key={c.id}
          className="ct-card clickable"
          onClick={() => onComms(c)}
          title={"View communications with " + c.name}
        >
          <span className={clsx("c-avatar", "side-" + c.side)}>
            {initials(c.name)}
          </span>
          <div className="ct-body">
            <div className="ct-name">{c.name}</div>
            <div className="ct-title">{c.title}</div>
            <a
              className="ct-email"
              href={"mailto:" + c.email}
              onClick={(ev) => ev.stopPropagation()}
            >
              {c.email}
            </a>
            <div className="ct-badges">
              {(c.newsletters || []).map((n) => (
                <span key={n} className="ct-badge nl" title="Gets this newsletter">✉ {n === "weekly" ? "Weekly briefing" : "Saga news"}</span>
              ))}
              {c.do_not_email && <span className="ct-badge stop">Do not email</span>}
              {!c.do_not_email && !c.email && <span className="ct-badge warn">No email</span>}
              {c.language && <span className="ct-badge">{c.language.toUpperCase()}</span>}
              {c.linkedin_url && (
                <a className="ct-badge link" href={c.linkedin_url} target="_blank" rel="noreferrer" onClick={(ev) => ev.stopPropagation()}>LinkedIn ↗</a>
              )}
              {c.mobile && <a className="ct-badge link" href={`https://wa.me/${c.mobile.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer"
                onClick={(ev) => ev.stopPropagation()} title={c.mobile}>WhatsApp ↗</a>}
            </div>
            {(c.relationship_owners || []).length > 0 && (
              <div className="ct-owners" title="Relationship owners">
                <AvatarStack ids={c.relationship_owners!} partners={partners} size={16} />
                {c.relationship_owners!.map((id) => partners[id]?.name.split(" ")[0] || id).join(", ")}
              </div>
            )}
          </div>
          <div className="ct-actions">
            <button
              className="icon-btn"
              title="Edit contact and relationship owners"
              onClick={(ev) => {
                ev.stopPropagation();
                onEdit(c);
              }}
            >
              {I.sliders({ size: 15 })}
            </button>
            <button
              className="icon-btn"
              title="Email"
              onClick={(ev) => {
                ev.stopPropagation();
                onEmail(c);
              }}
            >
              {I.send({ size: 15 })}
            </button>
            <button
              className="icon-btn"
              title="Communications"
              onClick={(ev) => {
                ev.stopPropagation();
                onComms(c);
              }}
            >
              {I.mail({ size: 15 })}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AccountDeals({
  deals,
  go,
}: {
  deals: Deal[];
  go: (screen: string, id?: string) => void;
}) {
  if (!deals.length)
    return (
      <div className="cd-empty">
        <p>No deals linked to this account. Opportunity knocks — are we listening?</p>
      </div>
    );

  return (
    <div className="acc-deals">
      {deals.map((d) => {
        const stageLabel =
          STAGES.find((s) => s.id === d.stage)?.label || d.stage;
        return (
          <div
            key={d.id}
            className="accd-row"
            onClick={() => go("project", d.id)}
          >
            <HeatMeter level={d.heat} />
            <div className="accd-body">
              <div className="accd-name">{d.name}</div>
              <div className="accd-sub">
                {d.principal_name} &middot; {stageLabel}
              </div>
            </div>
            <span className="accd-val mono">${(d.value_m ?? 0).toFixed(1)}M</span>
            {I.chevron({ size: 16, w: 1.8, style: { color: "var(--faint)" } })}
          </div>
        );
      })}
    </div>
  );
}

function ClientTodos({
  todos,
  partners,
  refetch,
}: {
  todos: Todo[];
  partners: Record<string, Partner>;
  refetch: () => void;
}) {
  if (!todos.length)
    return (
      <div className="cd-empty">
        <p>No to-dos here. Either you're on top of things, or blissfully unaware.</p>
      </div>
    );

  const toggle = async (id: string, done: boolean) => {
    await api.put("/api/todos/" + id, { done: !done });
    refetch();
  };

  return (
    <div className="client-todos">
      {todos.map((t) => {
        const who =
          t.owner === "all" || t.owner === "wait"
            ? null
            : partners[t.owner] || null;
        const overdue = !t.done && dueOverdue(t);
        return (
          <div key={t.id} className={clsx("ctd-row", t.done && "done")}>
            <button
              className={clsx("checkbox", t.done && "checked")}
              onClick={() => toggle(t.id, t.done)}
            >
              {t.done && I.check({ size: 13, w: 2.4 })}
            </button>
            <span className="ctd-task">{t.task}</span>
            {t.priority === "high" && !t.done && (
              <span className="task-pri">High</span>
            )}
            <span className={clsx("ctd-due", overdue && "overdue")}>
              {dueText(t)}
            </span>
            {who ? (
              <Avatar partner={who} size={22} />
            ) : (
              <span className="ti-owner-tag">
                {OWNER_LABEL[t.owner] || "—"}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ComposeEmail({
  c,
  close,
}: {
  c: Contact;
  close: () => void;
}) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await api.post("/api/emails/send", {
        to: c.email,
        subject: subject || "(no subject)",
        body,
        contact_id: c.id,
      });
      setSent(true);
      setTimeout(close, 800);
    } catch {
      setError("Failed to send. Make sure your Gmail is connected via /auth/google.");
      setSending(false);
    }
  };

  return (
    <div className="ai-overlay" onMouseDown={close}>
      <div className="compose-panel" onMouseDown={(e) => e.stopPropagation()}>
        <div className="compose-head">
          <div className="compose-title">New email</div>
          <span className="cmd-esc">esc</span>
        </div>
        <div className="compose-to">
          <span className="compose-to-label">To</span>
          <span className="compose-to-chip">
            {c.name} &middot; {c.email}
          </span>
        </div>
        <input
          className="compose-subject"
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <textarea
          className="compose-body"
          rows={7}
          placeholder="Write your message..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={sent}
        />
        {error && <div className="compose-error">{error}</div>}
        <div className="compose-foot">
          {sent ? (
            <span className="compose-sent">
              {I.check({ size: 14, w: 2.4 })}
              Sent
            </span>
          ) : (
            <>
              <button className="btn ghost sm" onClick={close} disabled={sending}>
                Cancel
              </button>
              <button
                className="btn primary sm"
                onClick={send}
                disabled={!body.trim() || sending}
              >
                {sending ? "Sending…" : <>{I.send({ size: 14 })} Send</>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---- Props ---- */

interface AccountsProps {
  go: (screen: string, id?: string) => void;
  openMinutes: (prefill?: any) => void;
  focusId: string | null;
  partners: Record<string, Partner>;
  /** Phone: show the list or one account, never both; tapping an account opens it as its own screen. */
  phone?: boolean;
}

/* ---- Main Accounts Component ---- */

export default function Accounts({
  go,
  openMinutes,
  focusId,
  partners,
  phone,
}: AccountsProps) {
  const { data: contactsData, refetch: refetchContacts } = useData<Contact[]>("/api/contacts");
  const { data: orgsData, refetch: refetchOrgs } = useData<Org[]>("/api/orgs");
  const { data: dealsData, refetch: refetchDeals } = useData<Deal[]>("/api/deals");
  const { data: emailsData } = useData<Email[]>("/api/emails");
  const { data: minutesData } = useData<Minutes[]>("/api/minutes");
  const { data: todosData, refetch: refetchTodos } = useData<Todo[]>("/api/todos");
  const { data: intelData } = useData<{ id: string; org_id: string | null; title: string; summary: string | null; created_at: string; url: string | null }[]>("/api/intel");

  const allContacts = contactsData || [];
  const allOrgs = orgsData || [];
  const allDeals = dealsData || [];
  const allEmails = emailsData || [];
  const allMinutes = minutesData || [];
  const allTodos = todosData || [];

  const accounts = useMemo(
    () => buildAccounts(allOrgs, allContacts, allDeals),
    [allOrgs, allContacts, allDeals]
  );

  // Resolve incoming focus: focusId may be a contact id or account id
  const focusContact = focusId
    ? allContacts.find((c) => c.id === focusId) || null
    : null;
  const initialAcc = focusContact
    ? accIdOfContact(focusContact)
    : focusId || (accounts[0]?.id || "");

  const [selId, setSelId] = useState(initialAcc);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("all");
  const [tab, setTab] = useState("comms");
  const [contactFocus, setContactFocus] = useState<string | null>(
    focusContact ? focusContact.name : null
  );
  const [compose, setCompose] = useState<Contact | null>(null);
  const [editingAcc, setEditingAcc] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);   // NEW_CONTACT = adding one
  const [ownerF, setOwnerF] = useState("all");   // relationship owner filter: all | partner id | none
  const ownerOptions = Object.values(partners)
    .filter((p) => p.active !== false)
    .map((p) => ({ value: p.id, label: p.name.split(" ")[0] }));
  const ownersField = { key: "relationship_owners", label: "Relationship owners", type: "people" as const, options: ownerOptions };
  const ownerNames = (ids: string[]) => ids.map((id) => partners[id]?.name.split(" ")[0] || id).join(", ");
  const [creatingAcc, setCreatingAcc] = useState(false);
  const refreshAccounts = () => { refetchOrgs(); refetchContacts(); refetchDeals(); };

  // Sync when focusId changes
  useEffect(() => {
    if (focusId) {
      const fc = allContacts.find((c) => c.id === focusId);
      setSelId(fc ? accIdOfContact(fc) : focusId);
      setContactFocus(fc ? fc.name : null);
      setTab("comms");
    }
  }, [focusId, allContacts]);

  // Select first account if nothing selected and accounts loaded
  useEffect(() => {
    if (!selId && accounts.length > 0) {
      setSelId(accounts[0].id);
    }
  }, [selId, accounts]);

  const types = useMemo(() => {
    const seen = new Set(accounts.map((a) => a.type));
    return ["all", ...Array.from(seen)];
  }, [accounts]);

  // An account counts as someone's when they own it, or own one of its contacts.
  const ownersOf = useCallback((a: Account) => {
    const ids = new Set(a.owners);
    a.contactIds.forEach((cid) => allContacts.find((c) => c.id === cid)?.relationship_owners?.forEach((o) => ids.add(o)));
    return ids;
  }, [allContacts]);

  const filtered = useMemo(
    () =>
      accounts
        .filter(
          (a) =>
            (ownerF === "all" || (ownerF === "none" ? ownersOf(a).size === 0 : ownersOf(a).has(ownerF))) &&
            (typeF === "all" || a.type === typeF) &&
            (!q ||
              (a.name + " " + a.markets.join(" "))
                .toLowerCase()
                .includes(q.toLowerCase()))
        )
        .sort(
          (x, y) =>
            y.dealIds.length - x.dealIds.length || x.name.localeCompare(y.name)
        ),
    [accounts, typeF, q, ownerF, ownersOf]
  );

  const newAccountDialog = creatingAcc && (
    <FormDialog
      title="New account"
      initial={{ name: "", type: "Operator", note: "", relationship_owners: "" }}
      fields={[
        { key: "name", label: "Name", required: true },
        { key: "type", label: "Type", type: "select", required: true, options: ORG_TYPES.map(t => ({ value: t, label: t })) },
        ownersField,
        { key: "note", label: "Description", type: "textarea" },
      ]}
      saveLabel="Create account"
      onClose={() => setCreatingAcc(false)}
      onSave={async (v) => {
        const org = await api.post<Org>("/api/orgs", v);
        setCreatingAcc(false);
        setSelId(org.id);
        refreshAccounts();
      }}
    />
  );

  const a = accounts.find((x) => x.id === selId) || accounts[0];
  if (!a) {
    return (
      <div className="screen accounts-screen">
        <div className="page-head">
          <div>
            <h1 className="page-title">Accounts</h1>
            <div className="page-sub">{orgsData && contactsData ? "No accounts yet" : "Loading..."}</div>
          </div>
          {orgsData && contactsData && (
            <button className="btn primary" onClick={() => setCreatingAcc(true)}>
              {I.plus({ size: 15 })}
              New account
            </button>
          )}
        </div>
        {newAccountDialog}
      </div>
    );
  }

  const contacts = a.contactIds
    .map((id) => allContacts.find((c) => c.id === id))
    .filter(Boolean) as Contact[];
  const deals = a.dealIds
    .map((id) => allDeals.find((d) => d.id === id))
    .filter(Boolean) as Deal[];
  const comms = [
    ...buildAccountComms(a, allContacts, allEmails, allMinutes, contactFocus),
    // Saga intelligence saved from the Radar under this account
    ...(contactFocus ? [] : (intelData || []).filter(i => i.org_id === a.id).map(i => ({
      kind: "note" as const, date: i.created_at, subject: `Intel: ${i.title}`, text: i.summary || "", who: "Saga Intelligence",
    }))),
  ].sort((x, y) => (y.date || "").localeCompare(x.date || ""));
  const todos = allTodos.filter(
    (t) => t.deal_id && a.dealIds.includes(t.deal_id)
  );
  const openTodos = todos.filter((t) => !t.done).length;
  const firstContact = contactFocus
    ? contacts.find((c) => c.name === contactFocus) || contacts[0]
    : contacts[0];

  const deleteAccount = async () => {
    const n = contacts.length;
    const extra = [
      n ? `${n} contact${n > 1 ? "s" : ""} will be archived (an admin can restore them in Settings).` : "",
      deals.length ? `${deals.length} deal${deals.length > 1 ? "s" : ""} will be kept but lose ${a.name} as counterparty.` : "",
    ].filter(Boolean).join("\n");
    if (!window.confirm(`Delete the account ${a.name}?${extra ? "\n\n" + extra : ""}`)) return;
    if (a.id.startsWith("x:")) {
      for (const c of contacts) await api.delete(`/api/contacts/${c.id}`);
    } else {
      await api.delete(`/api/orgs/${a.id}`);
    }
    setEditingAcc(false);
    setSelId("");
    refreshAccounts();
  };

  const startMinutes = () =>
    openMinutes &&
    openMinutes({
      contactId: firstContact?.id || undefined,
      dealId: deals[0]?.id || undefined,
      orgId: a.id.startsWith("x:") ? undefined : a.id,
    });

  return (
    <div className="screen accounts-screen">
      <div className="page-head">
        <div>
          <h1 className="page-title">Accounts</h1>
          <div className="page-sub">
            Organisations, their contacts, and every communication in one place
          </div>
        </div>
        <div className="cd-actions">
          <button className="btn ghost" onClick={() => setCreatingAcc(true)}>
            {I.plus({ size: 15 })}
            New account
          </button>
          <button className="btn primary" onClick={startMinutes}>
            {I.note({ size: 15, w: 1.8 })}
            New minutes
          </button>
        </div>
      </div>
      {newAccountDialog}

      <div className={clsx("accounts", phone && (focusId ? "ph-show-detail" : "ph-show-list"))}>
        {/* ---- account list ---- */}
        <div className="acc-list">
          <div className="cl-search">
            {I.search({ size: 14, w: 1.8, style: { color: "var(--muted)" } })}
            <input
              className="cl-search-input"
              placeholder="Search accounts..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="acc-types">
            {types.map((t) => (
              <button
                key={t}
                className={clsx("acc-type-chip", typeF === t && "active")}
                onClick={() => setTypeF(t)}
              >
                {t === "all" ? "All" : t}
              </button>
            ))}
          </div>
          <div className="acc-types acc-owner-filter" title="Relationship owner">
            {[["all", "Everyone"], ...ownerOptions.map((o) => [o.value, o.label]), ["none", "No owner"]].map(([id, label]) => (
              <button
                key={id}
                className={clsx("acc-type-chip", ownerF === id && "active")}
                onClick={() => setOwnerF(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="acc-rows">
            {filtered.map((ac) => (
              <div
                key={ac.id}
                className={clsx("acc-row", selId === ac.id && "sel")}
                onClick={() => {
                  if (phone) { go("clients", ac.id); return; }
                  setSelId(ac.id);
                  setContactFocus(null);
                  setTab("comms");
                }}
              >
                <span
                  className="acc-logo"
                  style={{
                    background: ACC_TYPE_C[ac.type] || "#5b6b7d",
                  }}
                >
                  {initials(ac.name)}
                </span>
                <div className="acc-body">
                  <div className="acc-name">{ac.name}</div>
                  <div className="acc-sub">
                    {ac.type} &middot; {ac.markets.join(", ") || "—"}
                  </div>
                </div>
                {ac.owners.length > 0 && <AvatarStack ids={ac.owners} partners={partners} size={18} />}
                {ac.dealIds.length > 0 && (
                  <span className="acc-dealn">{ac.dealIds.length}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ---- account detail ---- */}
        <div className="acc-detail">
          <div className="ad-head">
            <span
              className="ad-logo"
              style={{ background: ACC_TYPE_C[a.type] || "#5b6b7d" }}
            >
              {initials(a.name)}
            </span>
            <div className="ad-id">
              <h2 className="ad-name">{a.name}</h2>
              <div className="ad-meta">
                <span className="ad-type">{a.type}</span>
                {a.markets.length > 0 && (
                  <span className="mkt">
                    {I.globe({ size: 12, w: 1.7 })}
                    {a.markets.join(", ")}
                  </span>
                )}
                <span className="ad-cn">
                  {contacts.length} contact{contacts.length === 1 ? "" : "s"}
                </span>
                <button className="rel-owners link-btn" title="Relationship owners — click to change" onClick={() => setEditingAcc(true)}>
                  {a.owners.length > 0 ? (
                    <>
                      <AvatarStack ids={a.owners} partners={partners} size={18} />
                      <span className="rel-names">Relationship: {ownerNames(a.owners)}</span>
                    </>
                  ) : (
                    <span className="rel-none">No relationship owner</span>
                  )}
                </button>
              </div>
              {a.note && <div className="ad-note">{a.note}</div>}
            </div>
            <div className="cd-actions">
              {firstContact && (
                <button
                  className="btn ghost"
                  onClick={() => setCompose(firstContact)}
                >
                  {I.send({ size: 14 })}
                  Email
                </button>
              )}
              <button className="icon-btn ad-del" title="Delete account" aria-label={`Delete ${a.name}`} onClick={deleteAccount}>
                {I.trash({ size: 15 })}
              </button>
              <button className="btn ghost" onClick={() => setEditingAcc(true)}>
                {I.sliders({ size: 14 })}
                Edit
              </button>
              <button className="btn primary" onClick={startMinutes}>
                {I.note({ size: 14, w: 1.8 })}
                Minutes
              </button>
            </div>
          </div>
          {editingAcc && (
            <FormDialog
              title="Edit account"
              sub={a.id.startsWith("x:") ? "Saving turns this into a proper account its contacts are linked to." : undefined}
              initial={{ name: a.name, type: ORG_TYPES.includes(a.type) ? a.type : "Other", note: a.note || "", relationship_owners: a.owners.join(",") }}
              fields={[
                { key: "name", label: "Name", required: true },
                { key: "type", label: "Type", type: "select", required: true, options: ORG_TYPES.map(t => ({ value: t, label: t })) },
                ownersField,
                { key: "note", label: "Description", type: "textarea", placeholder: "Who they are, what they do, how we know them" },
              ]}
              onClose={() => setEditingAcc(false)}
              onSave={async (v) => {
                if (a.id.startsWith("x:")) {
                  const org = await api.post<Org>("/api/orgs", { ...v, adopt_contacts_named: a.name });
                  setSelId(org.id);
                } else {
                  await api.put(`/api/orgs/${a.id}`, v);
                }
                setEditingAcc(false);
                refreshAccounts();
              }}
              onDelete={deleteAccount}
            />
          )}

          {deals.length > 0 && (
            <div className="cd-deals">
              {deals.map((d) => (
                <button
                  key={d.id}
                  className="cd-deal"
                  onClick={() => go("project", d.id)}
                >
                  <HeatMeter level={d.heat} />
                  <span>{d.name}</span>
                  <span
                    className="cd-deal-prin"
                    style={{ color: d.principal_color || undefined }}
                  >
                    {d.principal_name}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="cd-tabs">
            {(
              [
                ["comms", "Communications", comms.length],
                ["contacts", "Contacts", contacts.length],
                ["deals", "Prospects", deals.length],
                ["todos", "To-dos", openTodos],
              ] as [string, string, number][]
            ).map((t) => (
              <button
                key={t[0]}
                className={clsx("cd-tab", tab === t[0] && "active")}
                onClick={() => setTab(t[0])}
              >
                {t[1]}
                <span className="cd-tab-n">{t[2]}</span>
              </button>
            ))}
          </div>

          {/* contact filter row (on comms) */}
          {tab === "comms" && contacts.length > 1 && (
            <div className="comm-filter">
              <button
                className={clsx("cf-chip", !contactFocus && "active")}
                onClick={() => setContactFocus(null)}
              >
                Whole account
              </button>
              {contacts.map((c) => (
                <button
                  key={c.id}
                  className={clsx("cf-chip", contactFocus === c.name && "active")}
                  onClick={() => setContactFocus(c.name)}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}

          <div className="cd-tabbody">
            {tab === "comms" && (
              <CommsTimeline comms={comms} onNew={startMinutes} go={go} />
            )}
            {tab === "contacts" && (
              <div className="ct-head">
                <button className="btn ghost sm" onClick={() => setEditingContact(NEW_CONTACT)}>{I.plus({ size: 13 })}New contact</button>
              </div>
            )}
            {tab === "contacts" && (
              <ContactsList
                contacts={contacts}
                partners={partners}
                onEdit={(c) => setEditingContact(c)}
                onEmail={(c) => setCompose(c)}
                onComms={(c) => {
                  setContactFocus(c.name);
                  setTab("comms");
                }}
              />
            )}
            {tab === "deals" && <AccountDeals deals={deals} go={go} />}
            {tab === "todos" && (
              <ClientTodos
                todos={todos}
                partners={partners}
                refetch={refetchTodos}
              />
            )}
          </div>
        </div>

        {compose && <ComposeEmail c={compose} close={() => setCompose(null)} />}
        {editingContact && (
          <ContactForm
            contact={editingContact === NEW_CONTACT ? null : editingContact}
            account={editingContact === NEW_CONTACT ? { id: a.id.startsWith("x:") ? null : a.id, name: a.name } : null}
            orgs={allOrgs} partners={partners}
            onClose={() => setEditingContact(null)}
            onSaved={(c) => {
              setEditingContact(null);
              refreshAccounts();
              if (c.org_id && c.org_id !== a.id) setSelId(c.org_id);
              setTab("contacts");
            }}
          />
        )}
      </div>
    </div>
  );
}
